import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import AppIcon from '../../../../../components/AppIcon';
import { PreviewSettlement } from '../../../../../api/Payroll/payroll';
import { PreviewSettlementInput, SettlementBreakdownView } from '../../../../../models/payroll';
import { formatNumber } from '../../../../../utils';
import { ScenarioMatrix, ScenarioColumn, ScenarioRow } from '../../components/ScenarioMatrix';

type Dimension = 'compensation' | 'deductions';

export interface SettlementScenarioBase {
  userId: string;
  noticePeriod: number;
  compensationInLieu: boolean;
  lastWorkingDay: string;
  totalDeductions: number;
  employerLoanDeductions?: number | null;
}

interface Props {
  base: SettlementScenarioBase;
  currencySymbol: string;
}

interface Column {
  key: string;
  label: string;
  sublabel?: string;
  isCurrent: boolean;
  input: PreviewSettlementInput;
}

interface ResolvedColumn extends Column {
  data: SettlementBreakdownView | null;
}

const uniqNums = (nums: number[]): number[] => {
  const seen = new Set<number>();
  return nums.filter(n => (seen.has(n) ? false : (seen.add(n), true)));
};

const SettlementScenarioComparison: React.FC<Props> = ({ base, currencySymbol }) => {
  const { t } = useTranslation('common');
  const hasDeductionsDim = base.totalDeductions > 0;
  const [dimension, setDimension] = useState<Dimension>('compensation');
  const [loading, setLoading] = useState(false);
  const [hasRun, setHasRun] = useState(false);
  const [error, setError] = useState('');
  const [columns, setColumns] = useState<ResolvedColumn[]>([]);

  const complete = !!base.lastWorkingDay;

  const baseInput = (over: Partial<Pick<PreviewSettlementInput, 'compensationInLieu' | 'totalDeductions'>>): PreviewSettlementInput => ({
    userId: base.userId,
    noticePeriod: base.noticePeriod,
    compensationInLieu: over.compensationInLieu ?? base.compensationInLieu,
    lastWorkingDay: base.lastWorkingDay,
    totalDeductions: over.totalDeductions ?? base.totalDeductions,
    employerLoanDeductions: base.employerLoanDeductions,
  });

  const buildColumns = (dim: Dimension): Column[] => {
    if (dim === 'deductions') {
      const variants = uniqNums([0, base.totalDeductions, base.totalDeductions * 2]);
      const labelFor = (v: number): string =>
        v === 0 ? t('No deductions') : v === base.totalDeductions ? t('As entered') : t('Doubled');
      return variants.map(v => ({
        key: `ded-${v}`,
        label: labelFor(v),
        sublabel: `${formatNumber(v)} ${currencySymbol}`.trim(),
        isCurrent: v === base.totalDeductions,
        input: baseInput({ totalDeductions: v }),
      }));
    }
    // compensation in lieu
    return [false, true].map(flag => ({
      key: `cil-${flag}`,
      label: flag ? t('With pay in lieu') : t('No pay in lieu'),
      isCurrent: flag === base.compensationInLieu,
      input: baseInput({ compensationInLieu: flag }),
    }));
  };

  const run = async (dim: Dimension) => {
    if (!complete) return;
    setLoading(true);
    setError('');
    const cols = buildColumns(dim);
    const results = await Promise.all(
      cols.map(async c => {
        try {
          const r = await PreviewSettlement(c.input);
          return r.errors?.length ? null : r.payload;
        } catch {
          return null;
        }
      }),
    );
    setColumns(cols.map((c, i) => ({ ...c, data: results[i] })));
    if (results.every(r => r === null)) setError(t('Comparison failed'));
    setHasRun(true);
    setLoading(false);
  };

  const selectDimension = (dim: Dimension) => {
    setDimension(dim);
    if (hasRun && complete) void run(dim);
  };

  const currentData = columns.find(c => c.isCurrent)?.data ?? null;
  const money = (v: number) => `${formatNumber(v)} ${currencySymbol}`.trim();

  const deltaChip = (col: ResolvedColumn): React.ReactNode => {
    if (col.isCurrent || !col.data || !currentData) return null;
    const d = col.data.finalSettlementAmount - currentData.finalSettlementAmount;
    if (Math.abs(d) < 0.005) return <span className="mt-0.5 block text-xs text-muted-foreground">{t('No change')}</span>;
    const up = d > 0;
    return (
      <span className={`mt-0.5 block text-xs font-medium ${up ? 'text-success' : 'text-destructive'}`}>
        {up ? '+' : '-'}
        {money(Math.abs(d))}
      </span>
    );
  };

  const cell = (col: ResolvedColumn, render: (d: SettlementBreakdownView) => React.ReactNode): React.ReactNode =>
    col.data ? render(col.data) : <span className="text-muted-foreground">—</span>;

  const matrixColumns: ScenarioColumn[] = columns.map(c => ({
    key: c.key,
    label: c.label,
    sublabel: c.sublabel,
    isCurrent: c.isCurrent,
  }));

  const rows: ScenarioRow[] = [
    {
      key: 'final',
      label: t('Total Settlement'),
      emphasis: true,
      cells: columns.map(c => (
        <div key={c.key} className="flex flex-col items-end">
          <span className="text-primary">{cell(c, d => money(d.finalSettlementAmount))}</span>
          {deltaChip(c)}
        </div>
      )),
    },
    {
      key: 'gratuity',
      label: t('Gratuity'),
      cells: columns.map(c => <span key={c.key}>{cell(c, d => money(d.gratuityAmount))}</span>),
    },
    {
      key: 'leave',
      label: t('Leave Encashment'),
      cells: columns.map(c => <span key={c.key}>{cell(c, d => money(d.leaveEncashmentAmount))}</span>),
    },
    {
      key: 'prorated',
      label: t('Prorated Final Month'),
      cells: columns.map(c => <span key={c.key}>{cell(c, d => money(d.proratedFinalMonthAmount))}</span>),
    },
    {
      key: 'cil',
      label: t('Compensation in Lieu'),
      cells: columns.map(c => <span key={c.key}>{cell(c, d => money(d.compensationInLieuAmount))}</span>),
    },
    {
      key: 'ded',
      label: t('Deductions'),
      cells: columns.map(c => (
        <span key={c.key}>
          {cell(c, d =>
            d.totalDeductions > 0 ? (
              <span className="text-destructive">-{money(d.totalDeductions)}</span>
            ) : (
              <span className="text-muted-foreground">—</span>
            ),
          )}
        </span>
      )),
    },
    {
      key: 'cap',
      label: t('Deduction cap applied'),
      cells: columns.map(c => (
        <span key={c.key}>
          {cell(c, d =>
            d.deductionCapApplied ? (
              <span className="inline-flex items-center rounded-full bg-warning/15 px-2 py-0.5 text-xs font-medium text-warning">
                {t('Capped')}
              </span>
            ) : (
              <span className="text-muted-foreground">—</span>
            ),
          )}
        </span>
      )),
    },
  ];

  const dimensions: { id: Dimension; label: string }[] = [
    { id: 'compensation', label: t('Compensation in Lieu') },
    ...(hasDeductionsDim ? [{ id: 'deductions' as Dimension, label: t('Deductions') }] : []),
  ];

  return (
    <div className="flex flex-col gap-4 rounded-xl border border-border bg-card p-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex items-start gap-3">
          <AppIcon name="Scale" size={20} className="mt-0.5 shrink-0 text-primary" />
          <div className="flex flex-col gap-0.5">
            <h4 className="text-sm font-semibold text-foreground">{t('What-if comparison')}</h4>
            <p className="max-w-[52ch] text-xs text-muted-foreground">
              {t('Compare the settlement outcome across scenarios. Nothing is saved.')}
            </p>
          </div>
        </div>
        {dimensions.length > 1 && (
          <div className="inline-flex flex-wrap gap-1 rounded-lg border border-border bg-muted/40 p-1" role="group" aria-label={t('Compare by')}>
            {dimensions.map(dim => (
              <button
                key={dim.id}
                type="button"
                onClick={() => selectDimension(dim.id)}
                disabled={!complete || loading}
                aria-pressed={dimension === dim.id}
                className={`rounded-md px-3 py-1.5 text-xs font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${
                  dimension === dim.id ? 'bg-card text-primary shadow-sm' : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                {dim.label}
              </button>
            ))}
          </div>
        )}
      </div>

      {!complete ? (
        <div className="flex items-center gap-2 rounded-lg border border-dashed border-border bg-muted/30 px-4 py-3 text-sm text-muted-foreground">
          <AppIcon name="Info" size={16} className="shrink-0" />
          <p>{t('Set the last working day above to compare scenarios.')}</p>
        </div>
      ) : !hasRun ? (
        <div>
          <button
            type="button"
            onClick={() => run(dimension)}
            disabled={loading}
            className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {loading && <AppIcon name="LoaderCircle" size={16} className="animate-spin" />}
            {t('Compare scenarios')}
          </button>
        </div>
      ) : (
        <>
          {error ? (
            <div className="flex items-center gap-2 rounded-lg border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive">
              <AppIcon name="AlertTriangle" size={16} className="shrink-0" />
              <p>{error}</p>
            </div>
          ) : (
            <ScenarioMatrix columns={matrixColumns} rows={rows} currentLabel={t('Current')} metricHeader={t('Scenario')} />
          )}
          <div>
            <button
              type="button"
              onClick={() => run(dimension)}
              disabled={loading}
              className="inline-flex items-center gap-2 text-xs font-medium text-primary hover:underline disabled:opacity-50"
            >
              {loading ? <AppIcon name="LoaderCircle" size={14} className="animate-spin" /> : <AppIcon name="RefreshCw" size={14} />}
              {t('Refresh comparison')}
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default SettlementScenarioComparison;
